import Card from '../../../../../accessControl/components/card'
import { RootState } from '../../../../../../rootReducer'
import { getShowLoader } from '../../../../../../reducers/requestReducer'
import { UNIQUE_WIDGET_INDEX, getUniqueWidgetIndex } from '../../../../mainDashboardv2/constants'
import {
  fetchPCIAccessControlData,
  ACTION_FETCH_PCI_ACCESS_CONTROL_DATA,
  fetchAttributeSetId
} from '../../../complianceDashboardSlice'
import {
  PCIFileData,
  PCIDatabaseData,
  PCIAccessControlData,
  AttributeSetData
} from '../../../queries'
import './pciComplianceSummary.less'
import LoaderView from '../../../../../../components/Loader/loader'
import historyService from '../../../../../../services/historyService'
import { getUpdatedUrl } from '../../../../../../utils/urlUtil'
import {
  FILES_GROUP_BY,
  FILTER_ACCESS,
  GENERIC_EMAIL_PARTNERS,
  GROUP_BY,
  PAGE_VIEW_KEY,
  PAGE_VIEW_VALUES,
  URL_ACCESS_CONTROL,
  URL_ATTRIBUTES_SET,
  URL_DATA_SOURCES,
  URL_DOCUMENTS_CLASSIFIED,
  URL_GROUPS,
  URL_INSIGHTS,
  URL_OVERVIEW,
  URL_PII_OBJECTS,
  URL_PRIVACY_PARTNERS,
  URL_USERS
} from '../../../../../../constants'
import { AccessControlAccessTypes } from '../../../../../accessControl/types'
import { useDashboardType } from '../../hooks/useDashboardType'
import { DashboardCategory } from '../../../../mainDashboardv2/slice'
import {
  ComplianceDashboardTypes,
  HIPAA_ATTRIBUTE_SET_NAME,
  PCI_ATTRIBUTE_SET_NAME
} from '../../../constants'
import { connect } from 'react-redux'
import { ArrowSquareOut as IconArrowSquareOut } from 'phosphor-react'
import { Link } from 'react-router-dom'
import { Icon, Tags, Tooltip, Typography } from '@lightbeamai/design-system'
import { useIntl } from 'react-intl'
import React, { useEffect } from 'react'

interface OwnProps {
  useMockData?: boolean
  widgetDatasourceId?: string
  widgetDashboardType?: string
  widgetDashboardId?: string
  attributeSetId?: string
}

interface StateProps {
  pciFilesData?: PCIFileData | null
  pciDatabasesData?: PCIDatabaseData | null
  pciAccessControlData?: PCIAccessControlData | null
  pciLastModifiedData?: any | null
  pciLastAccessedData?: any | null
  attributeSetData?: AttributeSetData | null
  showLoader: boolean
}

interface DispatchProps {
  fetchPCIAccessControlData: (params: {
    attributeSetId: string
    [UNIQUE_WIDGET_INDEX]?: string
  }) => void
  fetchAttributeSetId: (attributeSetName: string) => void
}

type PCIComplianceSummaryProps = StateProps & DispatchProps & OwnProps

const PCIComplianceSummary: React.FC<PCIComplianceSummaryProps> = (props) => {
  const {
    pciFilesData,
    pciDatabasesData,
    pciAccessControlData,
    pciLastModifiedData,
    pciLastAccessedData,
    attributeSetData,
    showLoader,
    useMockData = false,
    widgetDatasourceId,
    widgetDashboardId,
    widgetDashboardType,
    fetchPCIAccessControlData,
    fetchAttributeSetId
  } = props

  const intl = useIntl()

  const { isPciDashboard } = useDashboardType(widgetDashboardType as DashboardCategory)

  useEffect(() => {
    if (useMockData) return

    const attributeSetName = isPciDashboard ? PCI_ATTRIBUTE_SET_NAME : HIPAA_ATTRIBUTE_SET_NAME
    fetchAttributeSetId(attributeSetName)
  }, [useMockData, fetchAttributeSetId, isPciDashboard])

  useEffect(() => {
    if (useMockData) return
    if (!attributeSetData?.id) return
    fetchPCIAccessControlData({
      attributeSetId: attributeSetData?.id || '',
      [UNIQUE_WIDGET_INDEX]: getUniqueWidgetIndex(widgetDashboardId, widgetDatasourceId)
    })
  }, [
    useMockData,
    fetchPCIAccessControlData,
    attributeSetData,
    widgetDashboardId,
    widgetDatasourceId
  ])

  const getFilesNotAccessedCount = () => {
    if (!pciLastAccessedData || !Array.isArray(pciLastAccessedData)) return
    const item = pciLastAccessedData.find((item: any) => item.timeRange === 'LAST_6_MONTHS')
    return intl.formatNumber(item.count || 0, { notation: 'compact' })
  }
  const getFilesNotModifiedCount = () => {
    if (!pciLastModifiedData || !Array.isArray(pciLastModifiedData)) return
    const item = pciLastModifiedData.find((item: any) => item.timeRange === 'MORE_THAN_3_YEARS')
    return intl.formatNumber(item.count || 0, { notation: 'compact' })
  }

  const handleInventorySummaryClick = () => {
    historyService.push(
      getUpdatedUrl({
        pathName: `${URL_INSIGHTS}${URL_ATTRIBUTES_SET}/${attributeSetData?.id}${URL_DATA_SOURCES}`
      })
    )
  }

  const handleUsersClick = () => {
    historyService.push(
      getUpdatedUrl({
        pathName: `${URL_ACCESS_CONTROL}${URL_USERS}`,
        pageName: URL_USERS
      })
    )
  }

  const handleGroupsClick = () => {
    historyService.push(
      getUpdatedUrl({
        pathName: `${URL_ACCESS_CONTROL}${URL_GROUPS}`,
        pageName: URL_GROUPS
      })
    )
  }

  const handleAccessControlObjectsClick = (hasOpenAccess: boolean) => {
    historyService.push(
      getUpdatedUrl({
        pathName: `${URL_ACCESS_CONTROL}${URL_PII_OBJECTS}`,
        pageName: URL_PII_OBJECTS,
        pageQueryParams: {
          [FILTER_ACCESS]: hasOpenAccess
            ? AccessControlAccessTypes.open
            : AccessControlAccessTypes.excessive
        }
      })
    )
  }

  const handleExternallyShared = () => {
    historyService.push(
      getUpdatedUrl({
        pathName: `${URL_PRIVACY_PARTNERS}/${GENERIC_EMAIL_PARTNERS}${URL_OVERVIEW}`,
        pageName: URL_GROUPS
      })
    )
  }

  const handleRetentionClick = () => {
    historyService.push(
      getUpdatedUrl({
        pathName: `${URL_INSIGHTS}${URL_DOCUMENTS_CLASSIFIED}`,
        pageName: URL_DOCUMENTS_CLASSIFIED,
        queryParams: {
          [GROUP_BY]: FILES_GROUP_BY.CLASSIFICATION
        },
        pageQueryParams: {
          [PAGE_VIEW_KEY]: PAGE_VIEW_VALUES.list
        }
      })
    )
  }

  const attributeSetOverviewLink = `${URL_INSIGHTS}${URL_ATTRIBUTES_SET}/${attributeSetData?.id}${URL_OVERVIEW}`

  return (
    <Card
      className="compliance-summary-widget"
      cardTitle={
        <div className="widget-title">
          <div className="title-item">
            <Typography color="primary" variant="h6" as="span">
              {intl.formatMessage(
                {
                  id: 'compliance.dashboard.widget.complianceSummary.title'
                },
                {
                  type: isPciDashboard
                    ? ComplianceDashboardTypes.PCI
                    : ComplianceDashboardTypes.HIPAA
                }
              )}
            </Typography>
            <Tooltip
              title={intl.formatMessage({
                id: isPciDashboard
                  ? 'compliance.dashboard.pci.widget.title.tooltip'
                  : 'compliance.dashboard.hipaa.widget.title.tooltip'
              })}
              placement="right"
            >
              <Icon size={16} name="InfoCircle" className="pointer" />
            </Tooltip>
          </div>
          <Link to={attributeSetOverviewLink} className="title-item">
            <Typography color="primary" type="medium" variant="h6" as="a" className="view-all-link">
              {intl.formatMessage(
                {
                  id: 'compliance.dashboard.widget.complianceSummary.viewAll'
                },
                {
                  type: isPciDashboard
                    ? ComplianceDashboardTypes.PCI
                    : ComplianceDashboardTypes.HIPAA
                }
              )}
            </Typography>
            <IconArrowSquareOut size="16" color="var(--color-primary-light)" weight="bold" />
          </Link>
        </div>
      }
    >
      <div className="content-container">
        {/* Inventory Section */}
        <div className="content-section content-inventory">
          <Typography variant="base" color="placeholder">
            {intl.formatMessage({
              id: 'compliance.dashboard.pci.widget.complianceSummary.category.inventory'
            })}
          </Typography>
          <div className="section-content">
            <div onClick={handleInventorySummaryClick} className="inventory-card xs-mb-24">
              <div className="inventory-card-legend">
                <Tags color="lavender" size={20}>
                  <Icon name="File06" size={16} />
                </Tags>
                <Typography variant="baseMinus4" color="placeholder">
                  {intl.formatMessage({
                    id: 'compliance.dashboard.pci.widget.complianceSummary.summary.legend.inFiles'
                  })}
                </Typography>
              </div>
              <div className="inventory-card-content">
                <Typography as="span" variant="baseMinus2">
                  {intl.formatMessage({
                    id: 'compliance.dashboard.pci.widget.complianceSummary.summary.title.instances'
                  })}
                </Typography>
                <Typography variant="basePlus6" type="strong">
                  {intl.formatNumber(pciFilesData?.instancesInFilesCount || 0, {
                    notation: 'compact'
                  })}
                </Typography>
              </div>
            </div>

            <div onClick={handleInventorySummaryClick} className="inventory-card">
              <div className="inventory-card-legend">
                <Tags color="lavender" size={20}>
                  <Icon name="Table" size={16} />
                </Tags>
                <Typography variant="baseMinus4" color="placeholder">
                  {intl.formatMessage({
                    id:
                      'compliance.dashboard.pci.widget.complianceSummary.summary.legend.inDatabases'
                  })}
                </Typography>
              </div>
              <div className="inventory-card-content">
                <Typography as="span" variant="baseMinus2">
                  {intl.formatMessage({
                    id: 'compliance.dashboard.pci.widget.complianceSummary.summary.title.columns'
                  })}
                </Typography>
                <Typography variant="basePlus6" type="strong">
                  {intl.formatNumber(pciDatabasesData?.databaseStats?.totalColumnsCount || 0, {
                    notation: 'compact'
                  })}
                </Typography>
              </div>
            </div>
          </div>
        </div>

        {/* Access Section */}
        <div className="content-section content-access">
          <Typography variant="base" color="placeholder">
            {intl.formatMessage({
              id: 'compliance.dashboard.pci.widget.complianceSummary.category.access'
            })}
          </Typography>
          <div className="section-content">
            <div className="access-card-container">
              <div className="access-card">
                <Typography as="span" variant="baseMinus2" className="self-align-center">
                  {intl.formatMessage({
                    id:
                      'compliance.dashboard.pci.widget.complianceSummary.summary.title.accessGrantedTo'
                  })}
                </Typography>
                <div className="access-counts">
                  <div className="access-count-item" onClick={handleUsersClick}>
                    <Typography as="span" variant="basePlus6" type="strong">
                      {pciAccessControlData?.usersCount || 0}
                    </Typography>
                    <Icon size={20} name="User01" />
                  </div>
                  <div className="access-count-item" onClick={handleGroupsClick}>
                    <Typography as="span" variant="basePlus6" type="strong">
                      {pciAccessControlData?.groupsCount || 0}
                    </Typography>
                    <Icon size={20} name="Users01" />
                  </div>
                </div>
              </div>

              <div className="access-card" onClick={() => handleAccessControlObjectsClick(true)}>
                <Typography as="span" variant="baseMinus2" className="self-align-center">
                  {intl.formatMessage({
                    id:
                      'compliance.dashboard.pci.widget.complianceSummary.summary.title.filesWithOpenAccess'
                  })}
                </Typography>
                <Typography as="span" variant="basePlus6" type="strong">
                  {intl.formatNumber(pciAccessControlData?.openAccessObjectsCount || 0, {
                    notation: 'compact'
                  })}
                </Typography>
              </div>

              <div className="access-card" onClick={() => handleAccessControlObjectsClick(false)}>
                <Typography as="span" variant="baseMinus2" className="self-align-center">
                  {intl.formatMessage({
                    id:
                      'compliance.dashboard.pci.widget.complianceSummary.summary.title.filesWithExcessiveAccess'
                  })}
                </Typography>
                <Typography as="span" variant="basePlus6" type="strong">
                  {intl.formatNumber(pciAccessControlData?.excessAccessObjectsCount || 0, {
                    notation: 'compact'
                  })}
                </Typography>
              </div>

              <div className="access-card" onClick={handleExternallyShared}>
                <Typography as="span" variant="baseMinus2" className="self-align-center">
                  {intl.formatMessage({
                    id:
                      'compliance.dashboard.pci.widget.complianceSummary.summary.title.sharedExternally'
                  })}
                </Typography>
                <Typography as="span" variant="basePlus6" type="strong">
                  {intl.formatNumber(pciAccessControlData?.externallySharedCount || 0, {
                    notation: 'compact'
                  })}
                </Typography>
              </div>
            </div>
          </div>
        </div>

        {/* Retention Section */}
        <div className="content-section content-retention">
          <Typography variant="base" color="placeholder">
            {intl.formatMessage({
              id: 'compliance.dashboard.pci.widget.complianceSummary.category.retention'
            })}
          </Typography>
          <div className="section-content">
            <div className="rentention-card-container">
              <div onClick={handleRetentionClick} className="retention-card">
                <Typography variant="basePlus6" type="strong" as="span">
                  {getFilesNotAccessedCount() || 0}
                </Typography>
                <div className="retention-text">
                  <Typography variant="baseMinus2" as="span">
                    {intl.formatMessage({
                      id:
                        'compliance.dashboard.pci.widget.complianceSummary.summary.title.filesNotAccessed'
                    })}
                  </Typography>
                  <Typography variant="baseMinus2" as="span">
                    {intl.formatMessage({
                      id:
                        'compliance.dashboard.pci.widget.complianceSummary.summary.title.moreThan6Months'
                    })}
                  </Typography>
                </div>
              </div>

              <div onClick={handleRetentionClick} className="retention-card">
                <Typography variant="basePlus6" type="strong" as="span">
                  {getFilesNotModifiedCount() || 0}
                </Typography>
                <div className="retention-text">
                  <Typography variant="baseMinus2" as="span">
                    {intl.formatMessage({
                      id:
                        'compliance.dashboard.pci.widget.complianceSummary.summary.title.filesNotModified'
                    })}
                  </Typography>
                  <Typography variant="baseMinus2" as="span">
                    {intl.formatMessage({
                      id:
                        'compliance.dashboard.pci.widget.complianceSummary.summary.title.moreThan3Years'
                    })}
                  </Typography>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <LoaderView size="small" showLoader={showLoader} />
    </Card>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => {
  const widgetIndex = getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)

  return {
    pciFilesData: state.complianceDashboard.pciFilesData?.[widgetIndex],
    pciDatabasesData: state.complianceDashboard.pciDatabasesData?.[widgetIndex],
    pciAccessControlData: state.complianceDashboard.pciAccessControlData?.[widgetIndex],
    pciLastModifiedData: state.complianceDashboard.pciLastModifiedData?.[widgetIndex],
    pciLastAccessedData: state.complianceDashboard.pciLastAccessedData?.[widgetIndex],
    attributeSetData: state.complianceDashboard.attributeSetData,
    showLoader: getShowLoader(ACTION_FETCH_PCI_ACCESS_CONTROL_DATA)
  }
}

const mapDispatchToProps: DispatchProps = {
  fetchPCIAccessControlData,
  fetchAttributeSetId
}

export default connect(mapStateToProps, mapDispatchToProps)(PCIComplianceSummary)
